import React, { useState, useEffect } from "react";
import { HotTable } from "@handsontable/react";
import "handsontable/dist/handsontable.full.css";
import { registerAllModules } from "handsontable/registry";
import {
  Container,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  Grid,
  useMediaQuery,
  Dialog,
} from "@mui/material";
import { getProductsForCustomer, setCustomPricing, setCustomerTieredPricing // <-- Added endpoint for tiered pricing
 } from "../api/productApi";
import { getCustomers } from "../api/customerApi";
import { useLocalStorage } from "usehooks-ts";

registerAllModules();

interface TieredPrice {
  min_quantity: number;
  price_per_unit: number;
}

interface Product {
  productId: number;
  name: string;
  basePrice: number | null;
  customPrices: { [customerId: number]: number | string };
  variants?: ProductVariant[];
  expanded?: boolean;
  tiered_prices?: TieredPrice[];
}

interface ProductVariant {
  variantId: number;
  variantName: string;
  basePrice: number;
  customPrices: { [customerId: number]: number | string };
  tiered_prices?: TieredPrice[];
}

interface Customer {
  id: number;
  name: string;
  email: string;
  dispensary_name: string | null;
  phone: string | null;
  shipping_address: string | null;
  account_status: string;
  allow_subaccount_creation: boolean;
  master_customer_id: number | null;
}

interface BulkPriceControlsProps {
  bulkMode: "fixed" | "percentage";
  setBulkMode: React.Dispatch<React.SetStateAction<"fixed" | "percentage">>;
  bulkPrice: string;
  setBulkPrice: React.Dispatch<React.SetStateAction<string>>;
  onApply: () => void;
}

const BulkPriceControls: React.FC<BulkPriceControlsProps> = ({
  bulkMode,
  setBulkMode,
  bulkPrice,
  setBulkPrice,
  onApply,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
      <Grid container spacing={2} alignItems="center" direction={isMobile ? 'column' : 'row'}>
        <Grid size={{xs: 12, sm: 4}} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            label={bulkMode === "fixed" ? "Set Bulk Price" : "Set Discount (%)"}
            value={bulkPrice}
            onChange={(e) => setBulkPrice(e.target.value)}
            type="number"
            size={isMobile ? 'small' : 'medium'}
          />
        </Grid>
        <Grid size={{xs: 12, sm: 3}} sx={{ width: '100%' }}>
          <Button 
            fullWidth
            variant="outlined" 
            onClick={() => setBulkMode(prev => prev === "fixed" ? "percentage" : "fixed")}
            sx={{ minWidth: 120 }}
          >
            {bulkMode.toUpperCase()}
          </Button>
        </Grid>
        <Grid size={{xs: 12, sm: 5}} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={onApply}
            disabled={!bulkPrice}
            sx={{ minWidth: 160 }}
          >
            Apply to Selected
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

interface CustomerSelectorProps {
  customers: Customer[];
  selectedCustomers: number[];
  setSelectedCustomers: React.Dispatch<React.SetStateAction<number[]>>;
  loading: boolean;
  onLoadProducts: () => void;
}

const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  customers,
  selectedCustomers,
  setSelectedCustomers,
  loading,
  onLoadProducts,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{xs:12, md: 8}}>
          <FormControl fullWidth>
            <InputLabel>Select Master Accounts</InputLabel>
            <Select
              multiple
              value={selectedCustomers}
              onChange={(e) => setSelectedCustomers(e.target.value as number[])}
              renderValue={(selected) => `${selected.length} selected`}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    width: isMobile ? '95%' : 'auto',
                  },
                },
              }}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id} sx={{ maxWidth: '100%' }}>
                  <Box sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}>
                    {customer.name} ({customer.email})
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{xs: 12, md: 4}}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={onLoadProducts}
            disabled={!selectedCustomers.length || loading}
            sx={{ height: 56 }}
          >
            {loading ? <CircularProgress size={24} /> : "Load Products"}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const PricingManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tierModalOpen, setTierModalOpen] = useState(false);
const [selectedTierTarget, setSelectedTierTarget] = useState<{
  productId: number;
  variantId: number | null;
} | null>(null);
const [tierRows, setTierRows] = useState([{ min_quantity: "", price_per_unit: "" }]);

  const [storedCustomers, setStoredCustomers] = useLocalStorage<number[]>(
    "selectedCustomers",
    []
  );
  const [storedGridData, setStoredGridData] = useLocalStorage<any[][]>(
    "gridData",
    []
  );
  const [storedProducts, setStoredProducts] = useLocalStorage<Product[]>(
    "products",
    []
  );

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>(storedCustomers);
  const [products, setProducts] = useState<Product[]>(storedProducts);
  const [gridData, setGridData] = useState<any[][]>(storedGridData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkPrice, setBulkPrice] = useState<string>("");
  const [bulkMode, setBulkMode] = useState<"fixed" | "percentage">("fixed");
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [variantProductMap, setVariantProductMap] = useState<{ [key: number]: number }>({});
  const [notification, setNotification] = useState<{ message: string; severity: "success" | "error" } | null>(null);

  useEffect(() => {
    setStoredCustomers(selectedCustomers);
  }, [selectedCustomers]);

  useEffect(() => {
    setStoredGridData(gridData);
  }, [gridData]);

  useEffect(() => {
    setStoredProducts(products);
  }, [products]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const data: Customer[] = await getCustomers();
        const masterAccounts = data.filter(customer => customer.master_customer_id === null);
        setCustomers(masterAccounts);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setNotification({
          message: "Failed to load customers",
          severity: "error"
        });
      }
    };
    fetchCustomers();
  }, []);

const handleTierSave = async () => {
  if (!selectedTierTarget) return;
  try {
    await Promise.all(
      selectedCustomers.map((customerId) =>
        setCustomerTieredPricing({
          customerId,
          productId: selectedTierTarget.productId,
          tiers: tierRows
            .filter(t => t.min_quantity && t.price_per_unit)
            .map(t => ({
              minQuantity: parseInt(t.min_quantity),
              pricePerUnit: parseFloat(t.price_per_unit),
              ...(selectedTierTarget.variantId && { variantId: selectedTierTarget.variantId })
            }))
        })
      )
    );
    setNotification({ message: "Tiered pricing saved!", severity: "success" });
    setTierModalOpen(false);
  } catch (error) {
    console.error("Tier save failed", error);
    setNotification({ message: "Failed to save tiered pricing", severity: "error" });
  }
};




  const fetchProductsForSelectedCustomers = async () => {
    if (!selectedCustomers.length) return;
    setLoading(true);
    try {
      const productResponses = await Promise.all(
        selectedCustomers.map((customerId) => getProductsForCustomer(customerId))
      );
      const { mergedProducts, variantMap } = mergeProducts(productResponses);
      setVariantProductMap(variantMap);
      setProducts(mergedProducts);
      setGridData(formatGridData(mergedProducts));
    } catch (error) {
      console.error("Error fetching products:", error);
      setNotification({
        message: "Failed to load products",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

 const mergeProducts = (responses: any[]) => {
  const merged: { [key: number]: Product } = {};
  const variantMap: { [key: number]: number } = {};

  responses.forEach((products, index) => {
    const customerId = selectedCustomers[index];
    products.forEach((product: any) => {
      if (!merged[product.product_id]) {
        merged[product.product_id] = {
  productId: product.product_id,
  name: product.name,
  basePrice: product.base_price !== null ? Number(product.base_price) : 0,
  customPrices: {},
  variants: [],
  tiered_prices: product.tiered_prices?.map((t: any) => ({
    min_quantity: Number(t.min_quantity),
    price_per_unit: Number(t.price_per_unit),
  })) || []
};

      }

      merged[product.product_id].customPrices[customerId] = product.custom_price || "";

      (product.variants || []).forEach((variant: any) => {
        variantMap[variant.id] = product.product_id;
        let variantData = merged[product.product_id].variants?.find(v => v.variantId === variant.id);

        if (!variantData) {
          variantData = {
            variantId: variant.id,
            variantName: variant.variant_name,
            basePrice: Number(variant.price),
            customPrices: {},
tiered_prices: variant.tiered_prices?.map((t: any) => ({
  min_quantity: Number(t.min_quantity),
  price_per_unit: Number(t.price_per_unit),
})) || []
          };
          merged[product.product_id].variants?.push(variantData);
        }

        variantData.customPrices[customerId] = variant.custom_price || "";
      });
    });
  });

  return {
    mergedProducts: Object.values(merged),
    variantMap
  };
};


  const toggleProductExpand = (productId: number) => {
    setProducts(prev => 
      prev.map(product => 
        product.productId === productId 
          ? { ...product, expanded: !product.expanded } 
          : product
      )
    );
  };

  const formatGridData = (products: Product[]) => {
  return products.flatMap(product => {
    const productRow = [
      product.expanded ? "▼" : "▶",
      selectedProducts.has(`Product-${product.productId}`) ? "✔" : "",
      product.productId,
      "Product",
      product.name,
      `$${(product.basePrice ?? 0).toFixed(2)}`,
      "⚙️", // Tier edit button for product
      ...selectedCustomers.map(customerId => product.customPrices[customerId] || "")
    ];

    const variantRows = product.expanded 
      ? (product.variants || []).map(variant => [
          "",
          selectedProducts.has(`Variant-${variant.variantId}`) ? "✔" : "",
          variant.variantId,
          "Variant",
          `↳ ${variant.variantName}`,
          `$${(variant.basePrice ?? 0).toFixed(2)}`,
          "⚙️", // Tier edit button for variant
          ...selectedCustomers.map(customerId => variant.customPrices[customerId] || "")
        ])
      : [];

    return [productRow, ...variantRows];
  });
};


  const handleBulkPriceUpdate = () => {
    if (!bulkPrice || !selectedProducts.size) return;
    
    const newData = gridData.map(row => {
      const type = row[3];
      const id = row[2];
      const key = `${type}-${id}`;
      if (!selectedProducts.has(key)) return row;
      
      const basePrice = parseFloat(row[5].replace('$', ''));
      
      return row.map((cell, index) => {
        if (index <= 5) return cell;
        if (typeof cell !== 'string' && typeof cell !== 'number') return cell;
        
        if (bulkMode === "fixed") {
          return bulkPrice;
        } else {
          const discount = parseFloat(bulkPrice) / 100;
          return (basePrice * (1 - discount)).toFixed(2);
        }
      });
    });
    
    setGridData(newData);
  };

  const handleRowSelect = (startRow: number, startCol: number) => {
    const rowData = gridData[startRow];
    
    if (startCol === 0 && rowData[3] === "Product") {
      toggleProductExpand(rowData[2]);
      return;
    }

    if (startCol === 1) {
      const id = rowData[2];
      const type = rowData[3];
      const key = `${type}-${id}`;

      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.has(key) ? newSet.delete(key) : newSet.add(key);
        
        const newGridData = [...gridData];
        newGridData[startRow][1] = newSet.has(key) ? "✔" : "";
        setGridData(newGridData);
        
        return newSet;
      });
    }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const updates = gridData.flatMap(row => 
        selectedProducts.has(`${row[3]}-${row[2]}`) ? createPricingUpdates(row) : []
      );
      await Promise.all(updates.map(setCustomPricing));
      setNotification({ message: "Pricing updated successfully!", severity: "success" });
    } catch (error) {
      console.error("Save error:", error);
      setNotification({ message: "Failed to update pricing", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const createPricingUpdates = (row: any[]) => {
    const isVariant = row[3] === "Variant";
    const customerUpdates = selectedCustomers.map((customerId, index) => {
      const priceValue = row[6 + index];
      return {
        customerId,
        productId: isVariant ? variantProductMap[row[2]] : row[2],
        variantId: isVariant ? row[2] : undefined,
        customPrice: parseFloat(priceValue) || 0
      };
    });
    
    return customerUpdates.filter(update => !isNaN(update.customPrice));
  };

  useEffect(() => {
    if (products.length > 0) {
      setGridData(formatGridData(products));
    }
  }, [products, selectedCustomers, selectedProducts]);

  const tableSettings = {
    colHeaders: [
      "",
      "Select",
      "ID",
      "Type",
      "Name",
      "Base Price",
      "Tiers",
      ...selectedCustomers.map(id => {
        const customer = customers.find(c => c.id === id);
        return `CP ${customer?.name || id}`;
      })
    ],
    colWidths: isMobile
  ? [30, 50, 0, 80, 150, 80, 60, ...selectedCustomers.map(() => 160)]
  : [40, 60, 0, 100, 250, 120, 80, ...selectedCustomers.map(() => 200)],

      
    rowHeights: isMobile ? 35 : 40,
    licenseKey: "non-commercial-and-evaluation",
    stretchH: "all" as const,
    height: "100%",
    contextMenu: true,
    hiddenColumns: { columns: [2] },
    renderer: "text" as const,
    autoWrapRow: true,
    manualColumnResize: true,
    manualRowResize: true,
    filters: true,
    dropdownMenu: true,
    cells: (row: number, col: number) => ({
      readOnly: col !== 1 && col !== 5 && col < 6,
      className: "htMiddle htCenter"
    })
  };

  return (
    <Container maxWidth="xl" sx={{ 
      p: { xs: 1, sm: 2 },
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Typography variant="h4" gutterBottom sx={{ 
        mb: 2, 
        fontSize: { xs: '1.5rem', sm: '2rem' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Pricing Manager
      </Typography>

      <CustomerSelector
        customers={customers}
        selectedCustomers={selectedCustomers}
        setSelectedCustomers={setSelectedCustomers}
        loading={loading}
        onLoadProducts={fetchProductsForSelectedCustomers}
      />

      <BulkPriceControls
        bulkMode={bulkMode}
        setBulkMode={setBulkMode}
        bulkPrice={bulkPrice}
        setBulkPrice={setBulkPrice}
        onApply={handleBulkPriceUpdate}
      />

<Paper sx={{ 
  flex: 1,
  mb: 2,
  overflow: 'auto',
    overflowX: 'auto', 

  '& .handsontable': {
    minWidth: isMobile ? '100%' : '800px',
    fontSize: { xs: '12px', sm: '14px' },
    '& th, & td': {
      padding: { xs: '4px', sm: '8px' },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& th': {
      position: 'sticky',
      top: 0,
      backgroundColor: `${theme.palette.background.paper} !important`,
      zIndex: 1
    }
  }
}}>
  <HotTable
  data={gridData}
  {...tableSettings}
  afterOnCellMouseDown={(event, coords, td) => {
    const row = gridData[coords.row];
    const col = coords.col;

    // Handle expand/collapse toggle
    if (col === 0 && row[3] === "Product") {
      toggleProductExpand(row[2]);
      return;
    }

    // Handle select/deselect
    if (col === 1) {
      const id = row[2];
      const type = row[3];
      const key = `${type}-${id}`;

      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.has(key) ? newSet.delete(key) : newSet.add(key);
        const newGridData = [...gridData];
        newGridData[coords.row][1] = newSet.has(key) ? "✔" : "";
        setGridData(newGridData);
        return newSet;
      });
      return;
    }

    // Handle ⚙️ Tiers column
    if (col === 6) {
  const isVariant = row[3] === "Variant";
  const productId = isVariant ? variantProductMap[row[2]] : row[2];
  const variantId = isVariant ? row[2] : null;

  const tiers = isVariant
  ? products.find(p => p.productId === productId)?.variants?.find(v => v.variantId === variantId)?.tiered_prices
  : products.find(p => p.productId === productId)?.tiered_prices;

setSelectedTierTarget({ productId, variantId });
setTierRows(
  tiers?.length
    ? tiers.map((t: any) => ({
        min_quantity: String(t.min_quantity),
        price_per_unit: String(t.price_per_unit)
      }))
    : [{ min_quantity: "", price_per_unit: "" }]
);
setTierModalOpen(true);

  return;
}

  }}
/>

</Paper>


      <Box sx={{ 
        position: 'sticky',
        bottom: 0,
        py: 2,
        bgcolor: 'background.default',
        zIndex: 1
      }}>
        <Button
          fullWidth={isMobile}
          variant="contained"
          color="success"
          onClick={handleSavePricing}
          disabled={saving}
          size={isMobile ? "large" : "medium"}
        >
          {saving ? <CircularProgress size={24} /> : "Save Pricing"}
        </Button>
      </Box>
      <Dialog open={tierModalOpen} onClose={() => setTierModalOpen(false)} maxWidth="sm" fullWidth>
  <Box sx={{ p: 3 }}>
    <Typography variant="h6" gutterBottom>
      Set Tiered Pricing
    </Typography>

    {tierRows.map((row, index) => (
      <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
        <Grid size={{xs: 6}}>
          <TextField
            label="Min Quantity"
            type="number"
            fullWidth
            value={row.min_quantity}
            onChange={(e) => {
              const newRows = [...tierRows];
              newRows[index].min_quantity = e.target.value;
              setTierRows(newRows);
            }}
          />
        </Grid>
        <Grid size={{xs: 6}} >
          <TextField
            label="Price per Unit"
            type="number"
            fullWidth
            value={row.price_per_unit}
            onChange={(e) => {
              const newRows = [...tierRows];
              newRows[index].price_per_unit = e.target.value;
              setTierRows(newRows);
            }}
          />
        </Grid>
      </Grid>
    ))}

    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
      <Button onClick={() => setTierRows([...tierRows, { min_quantity: "", price_per_unit: "" }])}>
        + Add Row
      </Button>
      <Box>
        <Button onClick={() => setTierModalOpen(false)} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleTierSave}>
          Save Tiers
        </Button>
      </Box>
    </Box>
  </Box>
</Dialog>


      <Snackbar 
        open={!!notification} 
        autoHideDuration={3000} 
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification?.severity} sx={{ width: '100%' }}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PricingManager;